import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Calculator, BarChart3, Settings, Plus, X, CheckSquare, Square } from 'lucide-react';
import apiService from '../services/api';

interface AdminSettings {
  id: number;
  calculationConstant: number;
  isActive: boolean;
}

interface Beam {
  id: number;
  number: string;
  totalMeters: number;
  weight?: number;
  quality?: { id: number; name: string };
  qualityId?: number;
}

interface Quality {
  id: number;
  name: string;
  isActive: boolean;
}

interface CalculationResult {
  beamId: number;
  beamNumber: string;
  qualityId: number;
  qualityName: string;
  beamWeight: number;
  qualityWeight: number;
  beamCost: number;
  qualityCost: number;
  totalCostPerMeter: number;
  totalWeightPerMeter: number;
}

const CostCalculating: React.FC = () => {
  const { t } = useTranslation();
  
  // Fetch admin settings for calculation constant
  const { data: settingsResponse } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => apiService.get('/admin-settings'),
  });

  const adminSettings: AdminSettings = settingsResponse?.settings;
  const calculationConstant = adminSettings?.calculationConstant || 9000000;

  // Fetch beams
  const { data: beamsResponse } = useQuery({
    queryKey: ['beams'],
    queryFn: () => apiService.get('/beams'),
  });

  // Fetch qualities
  const { data: qualitiesResponse } = useQuery({
    queryKey: ['qualities'],
    queryFn: () => apiService.get('/qualities'),
  });

  const beams: Beam[] = beamsResponse?.beams || [];
  const qualities: Quality[] = (qualitiesResponse?.qualities || []).filter((q: Quality) => q.isActive);

  // Beam setting cards - array of card IDs with their settings
  const [beamSettingCards, setBeamSettingCards] = useState<Array<{
    id: string;
    warpDenier: string;
    tar: string;
    rate: string;
  }>>([{ id: '1', warpDenier: '', tar: '', rate: '' }]);

  // Quality setting cards - array of card IDs with their settings
  const [qualitySettingCards, setQualitySettingCards] = useState<Array<{
    id: string;
    weftDenier: string;
    pick: string;
    panna: string;
    rate: string;
  }>>([{ id: '1', weftDenier: '', pick: '', panna: '', rate: '' }]);
  
  // Final calculation inputs
  const [inputs, setInputs] = useState({
    totalMeters: '',
    gstRate: '18',
    // Meter calculation from total weights
    beamTotalWeightKg: '',
    qualityTotalWeightKg: ''
  });

  // Add new beam setting card
  const addBeamSettingCard = () => {
    const newId = Date.now().toString();
    setBeamSettingCards(prev => [...prev, { id: newId, warpDenier: '', tar: '', rate: '' }]);
  };

  // Remove beam setting card
  const removeBeamSettingCard = (cardId: string) => {
    if (beamSettingCards.length > 1) {
      setBeamSettingCards(prev => prev.filter(card => card.id !== cardId));
    }
  };

  // Update beam setting card
  const updateBeamSettingCard = (cardId: string, field: 'warpDenier' | 'tar' | 'rate', value: string) => {
    setBeamSettingCards(prev => prev.map(card => 
      card.id === cardId ? { ...card, [field]: value } : card
    ));
  };

  // Add new quality setting card
  const addQualitySettingCard = () => {
    const newId = Date.now().toString();
    setQualitySettingCards(prev => [...prev, { id: newId, weftDenier: '', pick: '', panna: '', rate: '' }]);
  };

  // Remove quality setting card
  const removeQualitySettingCard = (cardId: string) => {
    if (qualitySettingCards.length > 1) {
      setQualitySettingCards(prev => prev.filter(card => card.id !== cardId));
    }
  };

  // Update quality setting card
  const updateQualitySettingCard = (cardId: string, field: 'weftDenier' | 'pick' | 'panna' | 'rate', value: string) => {
    setQualitySettingCards(prev => prev.map(card => 
      card.id === cardId ? { ...card, [field]: value } : card
    ));
  };


  // Calculate results for all combinations
  const calculationResults = useMemo(() => {
    const results: CalculationResult[] = [];
    
    const beamMeter = 100; // Default to 100 meters
    const qualityMeter = 100; // Default to 100 meters

    // Calculate for each beam-quality combination from the cards
    beamSettingCards.forEach((beamCard, beamIndex) => {
      const beamWarpDenier = parseFloat(beamCard.warpDenier) || 0;
      const beamTar = parseFloat(beamCard.tar) || 0;
      const beamRate = parseFloat(beamCard.rate) || 0;

      // Skip if no values entered
      if (!beamWarpDenier && !beamTar && !beamRate) return;

      // Calculate beam weight per meter: (Warp Denier × Tar × Meter) / 9000000
      const beamWeightPerMeter = (beamWarpDenier * beamTar * beamMeter) / calculationConstant;
      const beamCostPerMeter = (beamWeightPerMeter * beamRate) / 100;

      qualitySettingCards.forEach((qualityCard, qualityIndex) => {
        const weftDenier = parseFloat(qualityCard.weftDenier) || 0;
        const pick = parseFloat(qualityCard.pick) || 0;
        const panna = parseFloat(qualityCard.panna) || 0;
        const qualityRate = parseFloat(qualityCard.rate) || 0;

        // Skip if no values entered
        if (!weftDenier && !pick && !panna && !qualityRate) return;

        // Calculate quality weight per meter: (Weft Denier × Pick × Panna × Meter) / 9000000
        const qualityWeightPerMeter = (weftDenier * pick * panna * qualityMeter) / calculationConstant;
        const qualityCostPerMeter = (qualityWeightPerMeter * qualityRate) / 100;

        const totalCostPerMeter = beamCostPerMeter + qualityCostPerMeter;
        const totalWeightPerMeter = beamWeightPerMeter + qualityWeightPerMeter;

        results.push({
          beamId: beamIndex + 1,
          beamNumber: `Beam ${beamIndex + 1}`,
          qualityId: qualityIndex + 1,
          qualityName: `Quality ${qualityIndex + 1}`,
          beamWeight: beamWeightPerMeter,
          qualityWeight: qualityWeightPerMeter,
          beamCost: beamCostPerMeter,
          qualityCost: qualityCostPerMeter,
          totalCostPerMeter,
          totalWeightPerMeter
        });
      });
    });

    return results;
  }, [beamSettingCards, qualitySettingCards, calculationConstant]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalMeters = parseFloat(inputs.totalMeters) || 0;
    const gstRate = parseFloat(inputs.gstRate) || 0;

    let totalCostPerMeter = 0;
    let totalWeightPerMeter = 0;
    let totalBeamCost = 0;
    let totalQualityCost = 0;
    let totalBeamWeight = 0;
    let totalQualityWeight = 0;

    calculationResults.forEach(result => {
      totalCostPerMeter += result.totalCostPerMeter;
      totalWeightPerMeter += result.totalWeightPerMeter;
      totalBeamCost += result.beamCost;
      totalQualityCost += result.qualityCost;
      totalBeamWeight += result.beamWeight;
      totalQualityWeight += result.qualityWeight;
    });

    const avgCostPerMeter = calculationResults.length > 0 ? totalCostPerMeter / calculationResults.length : 0;
    const avgWeightPerMeter = calculationResults.length > 0 ? totalWeightPerMeter / calculationResults.length : 0;

    const totalCostForMeters = avgCostPerMeter * totalMeters;
    const gstAmount = (avgCostPerMeter * gstRate) / 100;
    const totalCostWithGST = avgCostPerMeter + gstAmount;
    const gstAmountForMeters = (totalCostForMeters * gstRate) / 100;
    const totalCostForMetersWithGST = totalCostForMeters + gstAmountForMeters;

    // Calculate meters from total weights
    const beamTotalWeightKg = parseFloat(inputs.beamTotalWeightKg) || 0;
    const qualityTotalWeightKg = parseFloat(inputs.qualityTotalWeightKg) || 0;
    
    // Get average weight per 100m from calculations
    const avgBeamWeightPer100m = calculationResults.length > 0 ? (totalBeamWeight / calculationResults.length) * 100 : 0;
    const avgQualityWeightPer100m = calculationResults.length > 0 ? (totalQualityWeight / calculationResults.length) * 100 : 0;

    let metersFromBeamWeight = 0;
    let metersFromQualityWeight = 0;
    let totalMetersGenerated = 0;
    let limitingFactor = '';

    if (beamTotalWeightKg > 0 && avgBeamWeightPer100m > 0) {
      metersFromBeamWeight = (beamTotalWeightKg * 100) / avgBeamWeightPer100m;
    }

    if (qualityTotalWeightKg > 0 && avgQualityWeightPer100m > 0) {
      metersFromQualityWeight = (qualityTotalWeightKg * 100) / avgQualityWeightPer100m;
    }

    if (metersFromBeamWeight > 0 && metersFromQualityWeight > 0) {
      totalMetersGenerated = Math.min(metersFromBeamWeight, metersFromQualityWeight);
      limitingFactor = metersFromBeamWeight <= metersFromQualityWeight ? 'Beam Weight' : 'Quality Weight';
    } else if (metersFromBeamWeight > 0) {
      totalMetersGenerated = metersFromBeamWeight;
      limitingFactor = 'Beam Weight';
    } else if (metersFromQualityWeight > 0) {
      totalMetersGenerated = metersFromQualityWeight;
      limitingFactor = 'Quality Weight';
    }

    return {
      avgCostPerMeter,
      avgWeightPerMeter,
      totalBeamCost: calculationResults.length > 0 ? totalBeamCost / calculationResults.length : 0,
      totalQualityCost: calculationResults.length > 0 ? totalQualityCost / calculationResults.length : 0,
      totalBeamWeight: calculationResults.length > 0 ? totalBeamWeight / calculationResults.length : 0,
      totalQualityWeight: calculationResults.length > 0 ? totalQualityWeight / calculationResults.length : 0,
      totalCostForMeters,
      gstAmount,
      totalCostWithGST,
      gstAmountForMeters,
      totalCostForMetersWithGST,
      combinationsCount: calculationResults.length,
      // Meter calculation from weights
      avgBeamWeightPer100m,
      avgQualityWeightPer100m,
      metersFromBeamWeight,
      metersFromQualityWeight,
      totalMetersGenerated,
      limitingFactor
    };
  }, [calculationResults, inputs.totalMeters, inputs.gstRate, inputs.beamTotalWeightKg, inputs.qualityTotalWeightKg]);

  const handleInputChange = (field: keyof typeof inputs, value: string) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const resetAll = () => {
    setBeamSettingCards([{ id: '1', warpDenier: '', tar: '', rate: '' }]);
    setQualitySettingCards([{ id: '1', weftDenier: '', pick: '', panna: '', rate: '' }]);
    setInputs({
      totalMeters: '',
      gstRate: '18',
      beamTotalWeightKg: '',
      qualityTotalWeightKg: ''
    });
  };

  return (
    <div className="space-y-4 p-4 relative">
      {/* Floating Reset Button */}
      <button
        onClick={resetAll}
        className="fixed top-20 right-6 z-50 inline-flex items-center px-3 py-2 bg-red-50 border-2 border-red-300 rounded-lg text-sm font-medium text-red-600 hover:text-white hover:bg-red-600 transition-colors shadow-lg"
      >
        <X className="h-4 w-4 mr-1.5" />
        Reset All
      </button>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Left Column: Beam Settings */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900 flex items-center">
              <Calculator className="h-4 w-4 mr-2 text-green-600" />
              Beam Settings
            </h2>
            <button
              onClick={addBeamSettingCard}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md hover:bg-blue-50"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add More
            </button>
          </div>
          
          {beamSettingCards.map((card, index) => (
            <div key={card.id} className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-800">
                  Beam Setting {index + 1}
                </h3>
                {beamSettingCards.length > 1 && (
                  <button
                    onClick={() => removeBeamSettingCard(card.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Warp Denier
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={card.warpDenier}
                    onChange={(e) => updateBeamSettingCard(card.id, 'warpDenier', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Tar
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={card.tar}
                    onChange={(e) => updateBeamSettingCard(card.id, 'tar', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Beam Rate (₹ per kg)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={card.rate}
                    onChange={(e) => updateBeamSettingCard(card.id, 'rate', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="text-xs text-gray-500 pt-1.5 border-t border-gray-100">
                  Meter: 100 (default)
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Right Column: Quality Settings */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900 flex items-center">
              <Settings className="h-4 w-4 mr-2 text-blue-600" />
              Quality Settings
            </h2>
            <button
              onClick={addQualitySettingCard}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md hover:bg-blue-50"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add More
            </button>
          </div>
          
          {qualitySettingCards.map((card, index) => (
            <div key={card.id} className="bg-gradient-to-br from-white to-blue-50 rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-800">
                  Quality Setting {index + 1}
                </h3>
                {qualitySettingCards.length > 1 && (
                  <button
                    onClick={() => removeQualitySettingCard(card.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Weft Denier
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={card.weftDenier}
                    onChange={(e) => updateQualitySettingCard(card.id, 'weftDenier', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Pick
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={card.pick}
                    onChange={(e) => updateQualitySettingCard(card.id, 'pick', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Panna
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={card.panna}
                    onChange={(e) => updateQualitySettingCard(card.id, 'panna', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Quality Rate (₹ per kg)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={card.rate}
                    onChange={(e) => updateQualitySettingCard(card.id, 'rate', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="text-xs text-gray-500 pt-1.5 border-t border-gray-100">
                  Meter: 100 (default)
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Summary and Results Section - 3 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Summary Card */}
        <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 rounded-lg shadow-xl border-2 border-green-400 p-4">
          <h2 className="text-base font-bold text-green-900 mb-3 flex items-center">
            <BarChart3 className="h-4 w-4 mr-2" />
            Summary
          </h2>
          <div className="space-y-2.5">
            {/* Beam and Quality Details - First */}
            <div className="grid grid-cols-2 gap-2.5">
              {/* Beam Card */}
              <div className="bg-gradient-to-br from-orange-100 via-amber-50 to-orange-50 rounded-lg p-2.5 shadow-md border-2 border-orange-300">
                <div className="flex items-center justify-between mb-1.5">
                  <h3 className="text-xs font-bold text-orange-900">Beam</h3>
                  <span className="text-xs text-orange-700 font-semibold">₹</span>
                </div>
                <div className="space-y-1">
                  <div>
                    <div className="text-xs text-gray-700 mb-0.5 font-medium">Weight:</div>
                    <div className="text-sm font-bold text-orange-800">
                      {totals.totalBeamWeight.toFixed(2)} kg
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-700 mb-0.5 font-medium">Cost:</div>
                    <div className="text-sm font-bold text-orange-800">
                      ₹{totals.totalBeamCost.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Quality Card */}
              <div className="bg-gradient-to-br from-purple-100 via-primary-50 to-purple-50 rounded-lg p-2.5 shadow-md border-2 border-purple-300">
                <div className="flex items-center justify-between mb-1.5">
                  <h3 className="text-xs font-bold text-purple-900">Quality</h3>
                  <span className="text-xs text-purple-700 font-semibold">₹</span>
                </div>
                <div className="space-y-1">
                  <div>
                    <div className="text-xs text-gray-700 mb-0.5 font-medium">Weight:</div>
                    <div className="text-sm font-bold text-purple-800">
                      {totals.totalQualityWeight.toFixed(2)} kg
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-700 mb-0.5 font-medium">Cost:</div>
                    <div className="text-sm font-bold text-purple-800">
                      ₹{totals.totalQualityCost.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Per Meter Card */}
            <div className="bg-gradient-to-br from-green-200 via-emerald-100 to-green-100 rounded-lg p-2.5 shadow-md border-2 border-green-500">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-xs font-bold text-green-900">Total Per Meter</h3>
                <span className="text-xs text-green-700 font-semibold">₹</span>
              </div>
              <div className="space-y-1">
                <div>
                  <div className="text-xs text-gray-700 mb-0.5 font-medium">Weight:</div>
                  <div className="text-sm font-bold text-green-800">
                    {totals.avgWeightPerMeter.toFixed(2)} kg
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-700 mb-0.5 font-medium">Cost:</div>
                  <div className="text-sm font-bold text-green-800">
                    ₹{totals.avgCostPerMeter.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Stats - Second */}
            <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-200">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Combinations</div>
              <div className="text-xl font-bold text-gray-900">{totals.combinationsCount}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {beamSettingCards.length} beam{beamSettingCards.length !== 1 ? 's' : ''} × {qualitySettingCards.length} qualit{qualitySettingCards.length !== 1 ? 'ies' : 'y'}
              </div>
            </div>
            <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-200">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Avg Cost/Meter</div>
              <div className="text-xl font-bold text-green-700">
                ₹{totals.avgCostPerMeter.toFixed(2)}
              </div>
            </div>
            <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-200">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Avg Weight/Meter</div>
              <div className="text-xl font-bold text-green-700">
                {totals.avgWeightPerMeter.toFixed(2)} kg
              </div>
            </div>
          </div>
        </div>

        {/* Final Calculation */}
        <div className="bg-white rounded-lg shadow-lg border-2 border-blue-200 p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Final Calculation</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Total Meters
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={inputs.totalMeters}
                onChange={(e) => handleInputChange('totalMeters', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                GST Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={inputs.gstRate}
                onChange={(e) => handleInputChange('gstRate', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="18"
              />
            </div>
            
            {parseFloat(inputs.totalMeters) > 0 && (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border-2 border-blue-300 mt-3">
                <h3 className="text-xs font-bold text-blue-900 uppercase tracking-wide mb-2">Cost Breakdown</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-1">
                    <span className="text-xs font-medium text-gray-700">Base Cost:</span>
                    <span className="text-base font-bold text-gray-900">₹{totals.totalCostForMeters.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-xs font-medium text-gray-700">GST ({inputs.gstRate}%):</span>
                    <span className="text-base font-bold text-gray-900">₹{totals.gstAmountForMeters.toFixed(2)}</span>
                  </div>
                  <div className="pt-2 mt-2 border-t-2 border-blue-300 flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-900">Final Total:</span>
                    <span className="text-xl font-bold text-blue-700">₹{totals.totalCostForMetersWithGST.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Calculate Total Meters from Total Weights Section */}
        <div className="bg-gradient-to-br from-yellow-50 via-amber-50 to-yellow-100 rounded-lg shadow-xl border-2 border-yellow-400 p-4">
          <h2 className="text-base font-bold text-yellow-900 mb-2.5">Calculate Total Meters from Total Weights</h2>
          <p className="text-xs text-yellow-800 mb-2.5">
            Enter total weights (kg) to calculate how many meters can be produced based on weights per 100 meters
          </p>
          
          <div className="space-y-2.5 mb-2.5">
            <div>
              <label className="block text-xs font-semibold text-yellow-900 mb-1.5">
                Beam Total Weight (kg)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={inputs.beamTotalWeightKg}
                onChange={(e) => handleInputChange('beamTotalWeightKg', e.target.value)}
                className="w-full px-2.5 py-1.5 border-2 border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm bg-white"
                placeholder="e.g., 800"
              />
              {totals.avgBeamWeightPer100m > 0 && (
                <p className="text-xs text-yellow-700 mt-1 font-medium">
                  Weight per 100m: {totals.avgBeamWeightPer100m.toFixed(4)} kg
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-xs font-semibold text-yellow-900 mb-1.5">
                Quality Total Weight (kg)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={inputs.qualityTotalWeightKg}
                onChange={(e) => handleInputChange('qualityTotalWeightKg', e.target.value)}
                className="w-full px-2.5 py-1.5 border-2 border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm bg-white"
                placeholder="e.g., 200"
              />
              {totals.avgQualityWeightPer100m > 0 && (
                <p className="text-xs text-yellow-700 mt-1 font-medium">
                  Weight per 100m: {totals.avgQualityWeightPer100m.toFixed(4)} kg
                </p>
              )}
            </div>
          </div>

          {totals.totalMetersGenerated > 0 && (
            <div className="bg-white rounded-lg p-2.5 border-2 border-yellow-500 shadow-md">
              <div className="flex justify-between items-center mb-2 pb-1.5 border-b-2 border-yellow-400">
                <span className="text-xs font-bold text-yellow-900">Total Meters:</span>
                <span className="text-lg font-bold text-orange-600">
                  {totals.totalMetersGenerated.toFixed(2)} m
                </span>
              </div>
              <div className="space-y-1 text-xs">
                {totals.metersFromBeamWeight > 0 && (
                  <div className="flex justify-between items-center py-1 bg-gray-50 rounded px-2">
                    <span className="text-gray-700">Beam ({inputs.beamTotalWeightKg} kg):</span>
                    <span className="font-bold text-gray-900">{totals.metersFromBeamWeight.toFixed(2)} m</span>
                  </div>
                )}
                {totals.metersFromQualityWeight > 0 && (
                  <div className="flex justify-between items-center py-1 bg-gray-50 rounded px-2">
                    <span className="text-gray-700">Quality ({inputs.qualityTotalWeightKg} kg):</span>
                    <span className="font-bold text-gray-900">{totals.metersFromQualityWeight.toFixed(2)} m</span>
                  </div>
                )}
                {totals.limitingFactor && (
                  <div className="mt-1.5 pt-1.5 border-t border-yellow-400">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-yellow-800">Limiting:</span>
                      <span className="text-xs font-bold text-orange-700">{totals.limitingFactor}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Results - Compact Grid View */}
      {calculationResults.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg border-2 border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-5">Detailed Calculations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {calculationResults.map((result, index) => (
              <div key={`${result.beamId}-${result.qualityId}`} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border-2 border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-300">
                  <span className="text-xs font-bold text-gray-600 uppercase">#{index + 1}</span>
                  <span className="text-sm font-semibold text-green-700">₹{result.totalCostPerMeter.toFixed(2)}/m</span>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Beam:</span>
                    <span className="font-medium text-gray-900">{result.beamNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Quality:</span>
                    <span className="font-medium text-gray-900">{result.qualityName}</span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-gray-300 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Beam Wt:</span>
                      <span className="font-medium text-gray-900">{result.beamWeight.toFixed(2)} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Quality Wt:</span>
                      <span className="font-medium text-gray-900">{result.qualityWeight.toFixed(2)} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Beam Cost:</span>
                      <span className="font-medium text-gray-900">₹{result.beamCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Quality Cost:</span>
                      <span className="font-medium text-gray-900">₹{result.qualityCost.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CostCalculating;
